<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PlayerIconButton from '@/components/quran/player/PlayerIconButton.vue';
import PlayerProgress from '@/components/quran/player/PlayerProgress.vue';
import PlayerSleepTimer from '@/components/quran/player/PlayerSleepTimer.vue';
import PlayerSpeed from '@/components/quran/player/PlayerSpeed.vue';
import PlayerTransport from '@/components/quran/player/PlayerTransport.vue';
import PlayerVolume from '@/components/quran/player/PlayerVolume.vue';
import SurahArtwork from '@/components/quran/SurahArtwork.vue';
import UiButton from '@/components/ui/UiButton.vue';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';

/**
 * The player docked along the bottom of every page while a recitation is
 * queued — the whole width, like a music app: what is playing on the left,
 * the transport and progress in the middle, speed / sleep / volume on the
 * right. On a phone it folds to cover, title and play.
 */
const emit = defineEmits(['expand']);

const { t } = useI18n();
const player = useQuranPlayer();
const state = player.state;
const current = player.current;

const subtitle = computed(() => `${current.value.key} · ${player.reciter.value.name}`);
const position = computed(() => `${state.index + 1} / ${state.queue.length}`);
const pct = computed(() => `${Math.round(player.progress.value * 1000) / 10}%`);
</script>

<template>
  <aside
    class="fixed z-50 inset-x-0 bottom-0 bg-[#0d2626]/95 backdrop-blur-xl text-white border-t border-white/10 shadow-[0_-20px_50px_-20px_rgba(0,0,0,0.45)]"
    :aria-label="t('quran.player.label')"
  >
    <!-- Phone: progress line along the top edge. -->
    <div class="md:hidden absolute top-0 inset-x-0 h-[3px] bg-white/10" aria-hidden="true">
      <div class="h-full bg-deen-gold transition-[width] duration-200" :style="{ width: pct }" />
    </div>

    <div class="h-[72px] md:h-[88px] px-3 sm:px-5 grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)] items-center gap-3 md:gap-6">
      <button
        type="button"
        class="flex items-center gap-3 min-w-0 text-left rounded-2xl p-1 -m-1 hover:bg-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold"
        :aria-label="t('quran.player.expand')"
        @click="emit('expand')"
      >
        <SurahArtwork :surah="current.surah" size="sm" class="!w-12 !h-12 md:!w-14 md:!h-14" />
        <span class="min-w-0">
          <span class="flex items-center gap-2">
            <span class="text-sm font-semibold truncate">{{ current.surahName }}</span>
            <span v-if="state.isPlaying" class="hidden sm:flex items-end gap-[2px] h-3" aria-hidden="true">
              <span v-for="n in 3" :key="n" class="music-bar w-[2px] bg-deen-gold rounded-full" />
            </span>
          </span>
          <span class="block text-xs text-white/55 truncate tabular-nums">{{ subtitle }}</span>
        </span>
      </button>

      <!-- Phone: play and next only. -->
      <div class="flex md:hidden items-center gap-1">
        <UiButton
          intent="gold"
          icon-only
          class="!w-11 !h-11 rounded-full [&>iconify-icon]:!text-xl"
          :loading="state.isLoading"
          :icon="state.isPlaying ? 'solar:pause-bold' : 'solar:play-bold'"
          :aria-label="state.isPlaying ? t('quran.player.pause') : t('quran.player.play')"
          @click="player.toggle"
        />
        <PlayerIconButton icon="solar:skip-next-bold" :label="t('quran.player.next')" :disabled="!player.hasNext.value" @click="player.next" />
      </div>

      <div class="hidden md:flex flex-col items-center gap-0.5">
        <PlayerTransport size="sm" />
        <PlayerProgress />
      </div>

      <div class="hidden md:flex items-center justify-end gap-2">
        <span class="hidden xl:inline text-xs font-semibold text-white/40 tabular-nums mr-1">{{ position }}</span>
        <PlayerSpeed />
        <PlayerSleepTimer />
        <PlayerVolume class="hidden lg:flex" />
        <PlayerIconButton icon="solar:maximize-square-minimalistic-linear" size="sm" :label="t('quran.player.expand')" @click="emit('expand')" />
        <PlayerIconButton icon="solar:close-circle-linear" size="sm" :label="t('quran.player.stop')" @click="player.stop" />
      </div>
    </div>
  </aside>
</template>
